import {
  ChangeDetectionStrategy,
  Component,
  effect,
  forwardRef,
  input, InputSignal,
  output, OutputEmitterRef,
  signal,
  Signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { noop } from 'lodash-es';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatOptionSelectionChange } from '@angular/material/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TooltipIfTruncatedModule } from '../../../directives';
import { IsStringPipe } from '../../../pipes/is-string';
import { DEFAULT_KEY_VALUE_MODEL, KeyValueModel } from '../../../models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map, tap, throttleTime } from 'rxjs/operators';
import { MatTooltip } from '@angular/material/tooltip';

@UntilDestroy()
@Component({
  selector: 'app-autocomplete-dropdown',
  templateUrl: './autocomplete-dropdown.component.html',
  styleUrl: './autocomplete-dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteDropdownComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatProgressSpinner,
    MatInputModule,
    MatAutocompleteModule,
    ScrollingModule,
    MatButtonModule,
    FontAwesomeModule,
    TooltipIfTruncatedModule,
    MatTooltip,
    IsStringPipe
  ],
})
export class AutocompleteDropdownComponent implements ControlValueAccessor {
  cdkVirtualScrollViewport: Signal<CdkVirtualScrollViewport> = viewChild(CdkVirtualScrollViewport);
  matAutocompleteTrigger: Signal<MatAutocompleteTrigger> = viewChild(MatAutocompleteTrigger);
  inputControl: InputSignal<FormControl<KeyValueModel | string>> = input.required();
  label: InputSignal<string> = input.required();
  placeholder: InputSignal<string> = input('Type here to search');
  noResultsFoundText: InputSignal<string> = input('No results found');
  options: InputSignal<KeyValueModel[]> = input.required();
  isLoading: InputSignal<boolean> = input.required();
  isAutoBlurEnabled: InputSignal<boolean> = input(true);
  scrolledToEnd: OutputEmitterRef<void> = output();
  opened: OutputEmitterRef<void> = output();

  readonly emptyKeyValueModel: KeyValueModel = DEFAULT_KEY_VALUE_MODEL;

  readonly itemsSize: number = 48;

  selectedSignal: WritableSignal<KeyValueModel> = signal(DEFAULT_KEY_VALUE_MODEL);

  constructor() {
    effect(() => this.inputControl().setValue(this.selectedSignal()));
    effect(() => this.initScrollEvent(this.cdkVirtualScrollViewport()));
  }

  private initScrollEvent(viewport: CdkVirtualScrollViewport): void {
    if (!viewport) return;

    viewport.elementScrolled().pipe(
      throttleTime(50),
      map(() => viewport.measureScrollOffset('bottom')),
      filter((offset: number) => offset <= this.itemsSize),
      tap(() => this.scrolledToEnd.emit()),
      untilDestroyed(this)
    ).subscribe();
  }

  onOptionSelected(selection: MatOptionSelectionChange<KeyValueModel>): void {
    if (selection.source.selected) {
      this.commonChange(selection.source.value);
    }
  }

  private onChanged = noop;
  private onTouched = noop;

  registerOnChange(fn: (value: any) => void): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(value: KeyValueModel): void {
    if (value) {
      this.selectedSignal.set(value);
    }
  }

  displayFn(item: KeyValueModel): string {
    return item && !item.default && item.value ? item.value : '';
  }

  trackBy(index: number, item: KeyValueModel): string {
    return item.key;
  }

  clear(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.commonChange({...DEFAULT_KEY_VALUE_MODEL});
  }

  onOpen(): void {
    this.opened.emit();
  }

  private autoBlur(matAutocompleteTrigger: MatAutocompleteTrigger): void {
    if (this.isAutoBlurEnabled()) {
      window.setTimeout(() => {
        matAutocompleteTrigger.closePanel();
        matAutocompleteTrigger['_element'].nativeElement.blur();
      });
    }
  }

  private commonChange(value: KeyValueModel): void {
    this.selectedSignal.set(value);
    this.onChanged(value);
    this.onTouched();
    this.autoBlur(this.matAutocompleteTrigger());
  }
}
